import classnames from 'classnames';
import produce from 'immer';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import ResizeObserver from 'resize-observer-polyfill';

import { endThumbnailFlow, endVideoSettingsFlow } from 'actions/editor';
import {
  updateShareVideoInTrimMode,
  updateVideoIframeVisibility,
} from 'actions/shareVideo';
import { setSelectedPlayRangeHandle } from 'actions/trimming';
import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerEnterFullScreen,
  requestVideoPlayerLeaveFullScreen,
  requestVideoPlayerMute,
  requestVideoPlayerPause,
  requestVideoPlayerPlay,
  requestVideoPlayerUnmute,
  requestVideoPlayerVolume,
  toggleCaptionsVisible,
  updateTheaterMode,
  updateVideoPlayerBufferedParts,
  updateVideoPlayerCurrentTime,
  updateVideoPlayerEnded,
  updateVideoPlayerEnterFullScreen,
  updateVideoPlayerLeaveFullScreen,
  updateVideoPlayerMouseInactive,
  updateVideoPlayerMute,
  updateVideoPlayerNotWaiting,
  updateVideoPlayerPaused,
  updateVideoPlayerPlay,
  updateVideoPlayerPlaybackRate,
  updateVideoPlayerPlaying,
  updateVideoPlayerUnmute,
  updateVideoPlayerVolume,
  updateVideoPlayerWaiting,
} from 'actions/videoPlayer';
import { toggleViewerInsightsUI } from 'actions/viewerInsights';
import {
  EDIT_DELETE_ADD_CLIP,
  EDIT_UPDATE_TIME_RANGE,
  ENTERED_TRIM_MODE,
  FULL_SCREEN_CLICK,
  THEATER_MODE_TOGGLED,
  VIDEO_STARTED_WITH_CAPTIONS_OFF,
  VIDEO_STARTED_WITH_CAPTIONS_ON,
  VIDEO_START_NOISE_SUPPRESSION,
} from 'constants/events';
import {
  ROLLOUT_CONTINUE_WATCHING,
  ROLLOUT_VIDEO_PLAYER_END_SCREEN,
  ROLLOUT_WEAVE,
  VARIANT,
} from 'constants/featureFlag';
import * as hotkeys from 'constants/hotkeys';
import * as messages from 'constants/messages';
import { CAM } from 'constants/recordingTypes';
import { V6 } from 'constants/recordingVersions';
import { CLOUDFRONT_URI } from 'constants/routes';
import {
  MIN_PLAY_RANGE_DURATION,
  PLAY_RANGE_NUDGE_AMOUNT,
} from 'constants/trimming';
import {
  PLAYER_LONG_STEP,
  PLAYER_SHORT_STEP,
  ROOT_CLASS,
} from 'constants/videoPlayer';
import { clearCTAWidgetState } from 'creators/editor';
import { getFeatureFlagCreator } from 'creators/featureFlag';
import {
  deletePlayRangeCreator,
  enterTrimModeCreator,
  splitPlayRangeCreator,
  updatePlayRangesCreator,
} from 'creators/trimming';
import {
  skipBack,
  skipForward,
  trackVideoViewCreator,
  trackVideoWatchDurationCreator,
  updateVideoPlayerDimensionsCreator,
  updateVideoPlayerDurationCreator,
  updateVideoPlayerPasswordCreator,
  updateVideoPlayerSourceCreator,
  updateVideoPlayerSourceMp4Creator,
  updateVideoPlayerSourceRawCreator,
  videoPlayerEnterCommentMode,
  videoPlayerHideComments,
  watchAgain,
} from 'creators/videoPlayer';
import {
  selectCanDeletePlayRange,
  selectCanSplitPlayRange,
} from 'selectors/trimming';
import { selectIsActive } from 'selectors/viewerInsights';
import * as analytics from 'utilities/analytics';
import { getCloudfrontURI } from 'utilities/avatar';
import { isFirefox, isMobile } from 'utilities/device';
import {
  exitFullScreen,
  getFullScreenElement,
  listenForFullscreen,
  requestFullScreen,
  stopListeningForFullscreen,
} from 'utilities/dom';
import * as imageUtils from 'utilities/image';
import { rateToggler } from 'utilities/playSpeed';
import { waitingForTrim as waitingForTrimUtil } from 'utilities/trimming';
import VideoSession from 'utilities/video-session';

import avatarPlaceholder from 'img/icons/comments-avatar-placeholder.svg';
import { UrlQueryParamConsumer } from 'contexts/UrlQueryParamContext';

import ContinueWatchingObserver from './continue-watching-observer';
import ControlBar from './control-bar';
import EndScreen from './end-screen';
import InactiveModal from './inactive-modal';
import MouseCaptureWrapper from './mouse-capture';
import PasswordModal from './password-modal';
import PermissionModal from './permission-modal';
import PlayPauseOverlay from './play-pause-overlay';
import ContinueWatchingMobileBanner from './play-pause-overlay/continue-watching-mobile-banner';
import PlayScreen from './play-screen';
import TrimProgressPanel from './trim-progress-panel';
import Video from './video';
import WelcomeModal from './welcome-modal';
import FetchTranscript from '../share-video/transcript/transcript-list/fetchTranscript';
import FetchWaveform from '../video-player/waveform/FetchWaveform';
import { AllCommentsOverlay } from './all-comments';
import { EmbedContext } from '../embed-video';
import {
  handleToggleTranscriptCreator,
  handleUpdateCaptionsVisible,
} from '../../creators/shareVideo';
import { getShowCaptions } from '../../utilities/localStorage';
import { KEY_THEATER_MODE } from '../../../../shared/constants/cookie';
import { THUMB_VIDEO_PREVIEW } from '../../../../shared/constants/video';
import {
  FAILED,
  SHORT_DURATION,
} from '../../../../shared/constants/videoTranscript';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import { NewWaveformWrapper } from 'components/share-video/edit-tools-old/waveform-flag-wrapper';
import CircularLoadingSpinner from 'components/ui-components/circular-loading-spinner';
import EmbedTopbar from 'components/video-player/embed-top-bar';
import UploadProgress from 'components/video-player/upload-progress';

import './styles.less';

const VIDEO_PLAYER_PRELOAD_ICONS = [
  require('img/video-player/volume-0.svg'),
  require('img/video-player/volume-1.svg'),
  require('img/video-player/volume-2.svg'),
  require('img/video-player/pause.svg'),
  require('img/video-player/play-white.svg'),
  require('img/video-player/step-forward-five.svg'),
  require('img/video-player/step-back-five.svg'),
  require('img/video-player/exit-fullscreen.svg'),
  require('img/video-player/exit-fullscreen.svg'),
  require('img/video-player/fullscreen.svg'),
  require('img/video-player/big-play-button-play.svg'),
  require('img/video-player/replay.svg'),
];

const WELCOME_MODAL_KEYS = ['fromJoinRequest', 'invite'];

// if the mouse hasn't been moved over the player for 2 seconds, it's not active
const MOUSE_INACTIVE_TIMEOUT = 2000;

class VideoPlayer extends Component {
  constructor(props) {
    super(props);

    this.mouseInactiveTimeout = null;
    this.state = {
      containerDimensions: { x: 0 },
      showWelcomeModal: false,
    };
  }

  render() {
    const {
      autoplay,
      captionsSourceUrl,
      captionsVisible,
      controlsActive,
      ctaEnabled,
      ctaFlowActive,
      currentTime,
      currentTrimRange,
      recordReplyEnabled,
      EditVideoButtons,
      ViewerVideoButtons,
      ended,
      flippedCamera,
      fromLinkExpand,
      fromPublicSharePage,
      hasStarted,
      hideEmbedTopBar,
      iframeVisible,
      inTrimMode,
      isCameraRecording,
      isFetching,
      isInlineEmbedOnLoom,
      isLoggedIn,
      isMainVideoOnSharePage,
      needsPassword,
      needsPermission,
      parentLocation = '',
      password,
      paused,
      posterUrl,
      requestCurrentTime,
      requestMute,
      requestPause,
      requestPlay,
      requestPlaybackRate,
      requestUnmute,
      requestVolume,
      seeking,
      sourceMimeType,
      sourceUrl,
      trimRanges,
      updateCaptionsVisible,
      videoId,
      videoModel,
      videoName,
      waiting,
      waitingForTrim,
      fullScreen,
      showingAllComments,
      rolloutNewEndScreen,
    } = this.props;
    const { showWelcomeModal, containerDimensions } = this.state;

    const canShowCaptions = Boolean(captionsSourceUrl);

    const {
      video_properties: videoProperties = {},
      thumbnails = {},
      current_user_is_owner: currentUserIsOwner,
      owner_name: ownerName,
      owner_full_name: ownerFullname,
      active_status: isVideoActive,
    } = videoModel;
    const hasCustomThumbnail = thumbnails?.full;
    const { screen_type: screenType = '', isLoomGreeting } = videoProperties;
    const isWaiting = hasStarted && waiting && !paused;
    const showingEndScreen = !inTrimMode && ended && !ctaFlowActive;

    const videoThumbClass = classnames('video-player-thumbnail-image', {
      flipped: screenType === CAM && flippedCamera && !hasCustomThumbnail,
      customThumb: hasCustomThumbnail,
    });

    const ThumbDiv = posterUrl ? (
      <div
        className={videoThumbClass}
        style={{ backgroundImage: `url(${posterUrl})` }}
      />
    ) : (
      <div />
    );

    if (!inTrimMode && waitingForTrim) {
      return (
        <div
          className={ROOT_CLASS}
          style={this.props.stylesFromShareVideoParent}
        >
          <TrimProgressPanel EditVideoButtons={EditVideoButtons} />
        </div>
      );
    }

    if (!isMainVideoOnSharePage && !currentUserIsOwner && !isVideoActive) {
      return <InactiveModal ownerName={ownerName} ThumbDiv={ThumbDiv} />;
    }

    // do not show a loading indicator if we're just out of upload mode
    if (isFetching && videoModel.complete) {
      return (
        <div
          className={ROOT_CLASS}
          style={this.props.stylesFromShareVideoParent}
        >
          {ThumbDiv}
          <div className="video-player-overlay" />
          <CircularLoadingSpinner delay={1000} />
        </div>
      );
    }

    // if owner has set video to only be viewable by a certain
    // list of users we show the overlay modal on the share-page
    // instead
    if (needsPermission) {
      return isMainVideoOnSharePage ? null : <PermissionModal />;
    }

    if (password == null && needsPassword) {
      return <PasswordModal onPassword={this.onPassword} videoId={videoId} />;
    }

    const pauseScreenAvatar = videoModel.owner_avatar
      ? getCloudfrontURI(videoModel.owner_avatar.thumb)
      : avatarPlaceholder;

    // if the source url is the streamed webm object, it may get deleted while
    // the viewer is watching the video since we delete the streamed S3 object
    // once the non-streamed version has been uploaded, so we should replace with
    // the non-streamed object
    //
    // in all other cases, replace with MP4 in case there was a compatibility issue
    // playing the raw recording
    let onRefetchSource = this.props.updateVideoPlayerSourceMp4Creator;

    if (sourceUrl != null && sourceUrl.indexOf('-streamed.webm') > -1) {
      onRefetchSource = this.props.updateVideoPlayerSourceRawCreator;
    }

    if (!isVideoActive) {
      onRefetchSource = null;
    }

    const hideTopbarForMobileWhilePlayer = isMobile && hasStarted && !paused;

    const showPlayScreen =
      !ctaFlowActive &&
      !hasStarted &&
      // note(weave): This is temporarily added in order to allow V6 videos
      // to have edit buttons
      (sourceUrl || this.props.rolloutWeave) &&
      !showingAllComments &&
      !inTrimMode;

    const showEmbedTopBar =
      !isMainVideoOnSharePage &&
      !hideEmbedTopBar &&
      !hideTopbarForMobileWhilePlayer &&
      (controlsActive || showingEndScreen || showPlayScreen);
    const animatedPreviewUrl =
      !hasCustomThumbnail &&
      thumbnails[THUMB_VIDEO_PREVIEW] &&
      `${CLOUDFRONT_URI}/${thumbnails[THUMB_VIDEO_PREVIEW]}`;

    const showControlbar =
      !isMobile && (!showingEndScreen || rolloutNewEndScreen);

    return (
      <Fragment>
        <UploadProgress />

        <FetchTranscript />

        <NewWaveformWrapper>
          <FetchWaveform />
        </NewWaveformWrapper>

        <MouseCaptureWrapper
          onMouseMove={this.onMouseMove}
          onMouseLeave={this.updateVideoPlayerMouseInactiveImmediately}
          iframeVisible={iframeVisible}
          stylesFromShareVideoParent={this.props.stylesFromShareVideoParent}
          reference={player => (this.player = player)}
          fullScreen={fullScreen}
        >
          {!hasStarted && (posterUrl || animatedPreviewUrl) && (
            <>
              {animatedPreviewUrl && (
                <video
                  className={videoThumbClass}
                  loop
                  muted
                  playsInline
                  autoPlay
                >
                  <source src={animatedPreviewUrl} type="video/mp4"></source>
                </video>
              )}
              {!animatedPreviewUrl && (
                <div
                  className={videoThumbClass}
                  style={{ backgroundImage: `url(${posterUrl})` }}
                ></div>
              )}
            </>
          )}
          <FeatureFlaggedComponent
            flag={ROLLOUT_CONTINUE_WATCHING}
            activationValues={[true]}
          >
            <ContinueWatchingObserver />
          </FeatureFlaggedComponent>
          <Video
            currentTrimRange={currentTrimRange}
            EditVideoButtons={EditVideoButtons}
            ViewerVideoButtons={ViewerVideoButtons}
            flippedCamera={flippedCamera}
            fromLinkExpand={fromLinkExpand}
            hasStarted={hasStarted}
            inTrimMode={inTrimMode}
            isCameraRecording={isCameraRecording}
            isInlineEmbedOnLoom={isInlineEmbedOnLoom}
            isMainVideoOnSharePage={isMainVideoOnSharePage}
            parentLocation={parentLocation}
            showEmbedTopBar={showEmbedTopBar}
            trimRanges={trimRanges}
            videoId={videoId}
            videoModel={videoModel}
            // video properties
            autoplay={autoplay}
            ctaEnabled={ctaEnabled}
            currentTime={currentTime}
            ended={ended}
            ownerName={ownerFullname}
            pauseScreenAvatar={pauseScreenAvatar}
            paused={paused}
            seeking={seeking}
            sourceMimeType={sourceMimeType}
            sourceUrl={sourceUrl}
            // request properties
            requestCurrentTime={requestCurrentTime}
            requestMute={requestMute}
            requestPause={requestPause}
            requestPlay={requestPlay}
            requestPlaybackRate={requestPlaybackRate}
            requestUnmute={requestUnmute}
            requestVolume={requestVolume}
            // handlers
            onDimensionChange={this.props.updateVideoPlayerDimensionsCreator}
            onDoubleClick={this.handleToggleFullscreen}
            onDurationChange={this.props.updateVideoPlayerDurationCreator}
            onEnded={this.props.updateVideoPlayerEnded}
            onMute={this.props.updateVideoPlayerMute}
            onNotWaiting={this.props.updateVideoPlayerNotWaiting}
            onPause={this.props.updateVideoPlayerPaused}
            onPlay={this.props.updateVideoPlayerPlay}
            onPlaying={this.props.updateVideoPlayerPlaying}
            onProgress={this.props.updateVideoPlayerBufferedParts}
            onRateChange={this.props.updateVideoPlayerPlaybackRate}
            onRefetchSource={onRefetchSource}
            onTimeUpdate={this.props.updateVideoPlayerCurrentTime}
            onTrackVideoView={this.props.trackVideoViewCreator}
            onTrackVideoWatchDuration={
              this.props.trackVideoWatchDurationCreator
            }
            onUnmute={this.props.updateVideoPlayerUnmute}
            onVolumeChange={this.props.updateVideoPlayerVolume}
            onWaiting={this.props.updateVideoPlayerWaiting}
            captionsSourceUrl={captionsSourceUrl}
            captionsVisible={captionsVisible}
            width={containerDimensions.width}
            height={containerDimensions.height}
          />
          <PlayPauseOverlay />
          <ContinueWatchingMobileBanner />

          {showControlbar && (
            <ControlBar
              togglePictureInPicture={this.togglePictureInPicture}
              containerDimensions={containerDimensions}
              ended={ended}
              canShowCaptions={canShowCaptions}
              captionsVisible={captionsVisible}
              setCaptionsVisible={updateCaptionsVisible}
              onRef={ref => (this.controlBar = ref)}
              rolloutNewEndScreen={rolloutNewEndScreen}
            />
          )}
          {isWaiting && <CircularLoadingSpinner delay={1000} />}
          {showingEndScreen && (
            <EndScreen
              recordReplyEnabled={recordReplyEnabled}
              ctaEnabled={ctaEnabled}
              currentUserIsOwner={currentUserIsOwner}
              EditVideoButtons={EditVideoButtons}
              ViewerVideoButtons={ViewerVideoButtons}
              ended={ended}
              fromPublicSharePage={fromPublicSharePage}
              inTrimMode={inTrimMode}
              isInlineEmbedOnLoom={isInlineEmbedOnLoom}
              isLoggedIn={isLoggedIn}
              isLoomGreeting={isLoomGreeting}
              isMainVideoOnSharePage={this.props.isMainVideoOnSharePage}
              loomBrandedPlayer={videoModel.loom_branded_player}
              onRequestCurrentTime={this.props.requestVideoPlayerCurrentTime}
              onRequestPlay={this.props.requestVideoPlayerPlay}
              ownerFullname={ownerFullname}
              pauseScreenAvatar={pauseScreenAvatar}
              videoId={videoId}
              videoName={videoName}
              currentTime={parseInt(currentTime)}
              width={containerDimensions.width}
              height={containerDimensions.height}
            />
          )}
          {showPlayScreen && (
            <PlayScreen
              duration={this.props.duration}
              EditVideoButtons={EditVideoButtons}
              ViewerVideoButtons={ViewerVideoButtons}
              fullScreen={this.props.fullScreen}
              isLoading={requestPlay}
              onPlay={this.props.requestVideoPlayerPlay}
              ownerFullname={ownerFullname}
              pauseScreenAvatar={pauseScreenAvatar}
              theaterMode={this.props.theaterMode}
              videoName={videoModel.name}
              isMainVideoOnSharePage={this.props.isMainVideoOnSharePage}
            />
          )}
          {showEmbedTopBar && <EmbedTopbar />}
          {(!isMainVideoOnSharePage || this.props.fullScreen) && (
            <AllCommentsOverlay />
          )}
        </MouseCaptureWrapper>
        {showWelcomeModal && (
          <UrlQueryParamConsumer>
            {params =>
              WELCOME_MODAL_KEYS.some(key => params[key] === 'true') ? (
                <WelcomeModal />
              ) : null
            }
          </UrlQueryParamConsumer>
        )}
      </Fragment>
    );
  }

  onKeyDown = event => {
    // Avoid triggering hotkeys when user is typing
    const focusedOnInput =
      window.document.querySelector(
        'input:focus, select:focus, textarea:focus, [contenteditable]:focus'
      ) !== null;

    if (focusedOnInput) {
      return;
    }

    const modifierKeysPressed = event.ctrlKey || event.metaKey;

    const volumeUpAmount = 0.1;
    const volumeDownAmount = 0.1;

    if (!modifierKeysPressed) {
      if (hotkeys.VIDEO_PLAY_PAUSE.includes(event.key)) {
        // Prevent the default "spacebar" key behavior of
        // scrolling the page down
        event.preventDefault();
      }

      if (hotkeys.VIDEO_VOLUME_UP.includes(event.key)) {
        event.preventDefault();
        this.props.volume + volumeUpAmount <= 1 &&
          this.props.requestVideoPlayerVolume(
            this.props.volume + volumeUpAmount
          );
      }

      if (hotkeys.VIDEO_VOLUME_DOWN.includes(event.key)) {
        event.preventDefault();
        this.props.volume - volumeDownAmount >= 0 &&
          this.props.requestVideoPlayerVolume(
            this.props.volume - volumeDownAmount
          );
      }

      if (hotkeys.VIDEO_TOGGLE_SPEED.includes(event.key)) {
        event.preventDefault();
        rateToggler(this.props.playbackRate);
      }

      if (hotkeys.VIDEO_JUMP_BACK.includes(event.key)) {
        if (
          this.props.shouldNudgePlayRange &&
          event.key !== hotkeys.VIDEO_JUMP_BACKWARD_j_KEY
        ) {
          this.nudgePlayRange(-PLAY_RANGE_NUDGE_AMOUNT);
          event.preventDefault();
        }
      }

      if (hotkeys.VIDEO_JUMP_FORWARD.includes(event.key)) {
        if (
          this.props.shouldNudgePlayRange &&
          event.key !== hotkeys.VIDEO_JUMP_FORWARD_l_KEY
        ) {
          this.nudgePlayRange(PLAY_RANGE_NUDGE_AMOUNT);
          event.preventDefault();
        }
      }
    }
  };

  nudgePlayRange = amount => {
    let newCurrentTime;
    const newPlayRanges = produce(this.props.playRanges, draftRanges => {
      const range = draftRanges[this.props.selectedPlayRangeIdx];

      if (this.props.selectedPlayRangeHandle === 'left') {
        const prevRange = draftRanges[this.props.selectedPlayRangeIdx - 1];

        range.from = Math.min(
          Math.max(prevRange?.to || 0, range.from + amount),
          range.to - MIN_PLAY_RANGE_DURATION
        );
        newCurrentTime = range.from;
      } else {
        const nextRange = draftRanges[this.props.selectedPlayRangeIdx + 1];
        const duration = this.props.duration;

        range.to = Math.max(
          Math.min(nextRange?.from || duration, range.to + amount),
          range.from + MIN_PLAY_RANGE_DURATION
        );

        newCurrentTime = range.to;
      }
    });

    if (newCurrentTime) {
      this.props.requestVideoPlayerCurrentTime(newCurrentTime);
    }

    this.props.updatePlayRanges(newPlayRanges);

    analytics.track(EDIT_UPDATE_TIME_RANGE, {
      id: this.props.videoId,
      handleSide:
        this.props.selectedPlayRangeHandle === 'left' ? 'start' : 'end',
      type: 'handle',
      hotkey: true,
    });
  };

  onKeyUp = event => {
    const {
      hasStarted,
      updateCaptionsVisible,
      captionsVisible,
      transcriptVisible,
      inTrimMode,
      canSplitPlayRange,
      canDeletePlayRange,
    } = this.props;

    // Avoid triggering hotkeys when user is typing
    const focusedOnInput =
      document.querySelector(
        'input:focus, select:focus, textarea:focus, [contenteditable]:focus'
      ) !== null;

    if (focusedOnInput) {
      return;
    }

    const modifierKeysPressed = event.ctrlKey || event.metaKey;

    if (!modifierKeysPressed) {
      if (hotkeys.VIDEO_PLAY_PAUSE.includes(event.key)) {
        if (!hasStarted) {
          this.props.updateVideoPlayerPlaying();
          this.props.requestVideoPlayerPlay();

          return;
        }

        event.preventDefault();

        if (this.props.paused) {
          if (this.props.ended) {
            this.props.watchAgain();
          } else {
            this.props.requestVideoPlayerPlay();
          }
        } else {
          this.props.requestVideoPlayerPause();
        }
      }

      if (hotkeys.VIDEO_MUTE.includes(event.key)) {
        event.preventDefault();
        this.props.muted
          ? this.props.requestVideoPlayerUnmute()
          : this.props.requestVideoPlayerMute();
      }

      if (hotkeys.VIDEO_TOGGLE_CAPTIONS.includes(event.key)) {
        event.preventDefault();
        updateCaptionsVisible(!captionsVisible);
      }

      if (hotkeys.VIDEO_STEP_FORWARD.includes(event.key)) {
        event.preventDefault();
        this.props.skipForward(PLAYER_SHORT_STEP);
      }

      if (hotkeys.VIDEO_STEP_BACK.includes(event.key)) {
        event.preventDefault();
        this.props.skipBack(PLAYER_SHORT_STEP);
      }

      if (
        this.props.activeVideoTranscriptId &&
        this.props.transcriptStatus !== FAILED &&
        this.props.transcriptStatus !== SHORT_DURATION &&
        this.props.videoReadyToPlayOnSharePage &&
        this.props.userCanAccessTranscript &&
        hotkeys.VIDEO_TOGGLE_TRANSCRIPT.includes(event.key)
      ) {
        event.preventDefault();

        if (event.key === 'Escape') {
          if (transcriptVisible) {
            this.props.handleToggleTranscriptCreator();
          }
        } else {
          if (this.props.areViewerInsightsOpen) {
            this.props.toggleViewerInsightsUI();
          }

          if (this.props.areVideoSettingsOpen) {
            this.props.endVideoSettingsFlow();
          }

          if (this.props.areVideoCtaSettingsOpen) {
            this.props.clearCTAWidgetState();
          }

          if (this.props.areThumbnailSettingsOpen) {
            this.props.endThumbnailFlow();
          }

          this.props.handleToggleTranscriptCreator();
        }
      }

      if (
        this.props.commentsEnabled &&
        hotkeys.VIDEO_ENTER_COMMENT_MODE.includes(event.key)
      ) {
        event.preventDefault();
        this.props.enterCommentMode();
      }

      if (!inTrimMode) {
        if (hotkeys.VIDEO_TOGGLE_FULLSCREEN.includes(event.key)) {
          event.preventDefault();

          // In Firefox, we must request full screen in the scope of the event
          // handler, otherwise the following error gets thrown:
          //
          // Request for fullscreen was denied because Element.requestFullscreen()
          // was not called from inside a short running user-generated event handler.
          //
          // In this case we let the code continue and let the error still happen
          // to make sure that all flows and reducers states remain consistent
          if (isFirefox) {
            this.player.mozRequestFullScreen();
          }

          this.props.fullScreen
            ? this.props.requestVideoPlayerLeaveFullScreen()
            : this.props.requestVideoPlayerEnterFullScreen();
        }

        if (hotkeys.VIDEO_TOGGLE_THEATER.includes(event.key)) {
          event.preventDefault();
          this.toggleTheater();
        }

        if (hotkeys.VIDEO_PICTURE_IN_PICTURE.includes(event.key)) {
          event.preventDefault();
          this.togglePictureInPicture();
        }

        if (
          hotkeys.VIDEO_ENTER_TRIM_MODE.includes(event.key) &&
          this.props.currentUserIsOwner
        ) {
          event.preventDefault();
          this.enterTrimMode();
        }
      }

      if (inTrimMode) {
        if (
          this.props.shouldNudgePlayRange &&
          hotkeys.TRIM_DESELECT_HANDLE.includes(event.key)
        ) {
          this.props.updateSelectedPlayRangeHandle(null);
        }

        if (
          hotkeys.TRIM_SPLIT_PLAY_RANGE.includes(event.key) &&
          canSplitPlayRange
        ) {
          this.props.splitPlayRange();

          analytics.track(EDIT_UPDATE_TIME_RANGE, {
            id: this.props.videoId,
            type: 'split',
            hotkey: true,
            has_been_trimmed: this.props.trimRanges.length > 0,
            event_type: 'key',
          });
        }

        if (
          hotkeys.TRIM_DELETE_PLAY_RANGE.includes(event.key) &&
          canDeletePlayRange
        ) {
          this.props.deletePlayRange();

          analytics.track(EDIT_DELETE_ADD_CLIP, {
            id: this.props.videoId,
            has_been_trimmed: this.props.trimRanges.length > 0,
            event_type: 'click',
          });
        }
      }
    }
  };

  enterTrimMode = () => {
    const { videoId, lastSavedTrimRanges: trimRanges } = this.props;

    this.props.enterTrimMode(trimRanges);

    this.props.updateShareVideoInTrimMode(true);

    analytics.track(ENTERED_TRIM_MODE, {
      id: videoId,
      has_been_trimmed: trimRanges.length > 0,
    });
  };

  toggleChromePictureInPicture = async videoElm => {
    if (!document.pictureInPictureEnabled) {
      return;
    }

    if (this.props.chromePipEnabled) {
      try {
        await document.exitPictureInPicture();
      } catch (err) {
        console.error('Unable to exit Picture in Picture', err);

        return;
      }
    } else {
      try {
        await videoElm.requestPictureInPicture();
      } catch (err) {
        console.error('Unable to enable Picture in Picture', err);

        return;
      }
    }
  };

  toggleSafariPictureInPicture = videoElm => {
    if (
      videoElm.webkitSupportsPresentationMode &&
      typeof videoElm.webkitSetPresentationMode === 'function'
    ) {
      const isInPip = videoElm.webkitPresentationMode === 'picture-in-picture';

      videoElm.webkitSetPresentationMode(
        isInPip ? 'inline' : 'picture-in-picture'
      );
    }
  };

  togglePictureInPicture = () => {
    const videoElms = document.getElementsByTagName('video') || [];
    const videoElm = videoElms[0];

    if (!videoElm) {
      return;
    }

    this.toggleChromePictureInPicture(videoElm);
    this.toggleSafariPictureInPicture(videoElm);
  };

  toggleTheater = () => {
    const { currentUserIsOwner, theaterMode, inTrimMode } = this.props;
    const newTheaterModeVal = inTrimMode ? false : !theaterMode;

    document.cookie = `${KEY_THEATER_MODE} = ${newTheaterModeVal};`;

    this.props.updateTheaterMode(newTheaterModeVal, this.props.inTrimMode);
    analytics.track(THEATER_MODE_TOGGLED, {
      currentUserIsOwner,
      on: newTheaterModeVal,
    });
  };

  handleToggleFullscreen = () => {
    if (this.props.fullScreen) {
      this.props.requestVideoPlayerLeaveFullScreen();
    } else {
      this.props.requestVideoPlayerEnterFullScreen();
    }

    analytics.track(FULL_SCREEN_CLICK, {
      from_double_click: true,
      full_screen: this.props.fullScreen,
    });
  };

  onPassword = password => {
    this.props.updateVideoPlayerPasswordCreator(password);
    this.props.updateVideoPlayerSourceCreator();
  };

  togglePlay = () => {
    const { currentTime } = this.props;

    if (this.props.paused) {
      VideoSession.sendEvent('play', { currentTime });
      this.props.requestVideoPlayerPlay();
    } else {
      VideoSession.sendEvent('pause', { currentTime });
      this.props.requestVideoPlayerPause();
    }
  };

  onFullscreenChange = () => {
    const fsElement = getFullScreenElement();

    if (fsElement !== this.player) {
      this.props.updateVideoPlayerLeaveFullScreen();
    } else {
      this.props.updateVideoPlayerEnterFullScreen();
    }
  };

  onMouseMove = () => {
    this.setupMouseInactiveTimeout();
  };

  setupMouseInactiveTimeout() {
    window.clearTimeout(this.mouseInactiveTimeout);

    this.mouseInactiveTimeout = window.setTimeout(() => {
      if (this.controlBar != null && this.controlBar.getControlBarEl != null) {
        const controlBar = this.controlBar.getControlBarEl();
        const watchOnLoom = document.getElementsByClassName('watch-on-loom')[0];
        const isHoveringWatchOnLoom =
          watchOnLoom && watchOnLoom.querySelector(':hover');
        const isHoveringWatchOnControlBar =
          controlBar && controlBar.querySelector(':hover');

        // do not make the player inactive if the control bar is being hovered:
        // https://stackoverflow.com/a/14800287/696130
        if (isHoveringWatchOnLoom || isHoveringWatchOnControlBar) {
          return;
        }
      }

      this.props.updateVideoPlayerMouseInactive();
    }, MOUSE_INACTIVE_TIMEOUT);
  }

  componentDidUpdate(prevProps) {
    const {
      autoplay,
      ended,
      iframeVisible,
      isFetching,
      isMainVideoOnSharePage,
      needsIntentSurvey,
      requestEnterFullScreen,
      requestLeaveFullScreen,
      requestVideoPlayerPlay,
      sourceRequested,
      updateVideoPlayerSourceCreator,
      videoModel,
      captionsSourceUrl,
      hasStarted,
      videoId,
    } = this.props;

    // When the video ends, show the user a welcome modal if they haven't
    // completed their intent survey yet.
    if (
      ended &&
      !prevProps.ended &&
      // So that we don't show this on /embed
      isMainVideoOnSharePage
    ) {
      if (needsIntentSurvey) {
        this.setState({ showWelcomeModal: true });
      }
    }

    // only trigger this if video was uploading
    if (iframeVisible && !videoModel.complete && !sourceRequested) {
      updateVideoPlayerSourceCreator();
    }

    // when entering fullscreen mode, we want to fullscreen on the containing
    // element (this player) rather than the video element itself - otherwise,
    // we will get media elements in Safari that cannot be hidden
    if (requestEnterFullScreen) {
      requestFullScreen(this.player);
    } else if (requestLeaveFullScreen) {
      exitFullScreen();
    }

    // force videoplayer to try and play if autoplay is enabled
    if (
      autoplay &&
      iframeVisible &&
      videoModel.complete &&
      prevProps.isFetching &&
      !isFetching &&
      !this.props.inTrimMode
    ) {
      requestVideoPlayerPlay();
    }

    // If we fetch captions before the user first starts playing the video
    if (!prevProps.hasStarted && hasStarted && captionsSourceUrl) {
      const captionsVisibilityInStorage = getShowCaptions();

      if (captionsVisibilityInStorage) {
        analytics.track(VIDEO_STARTED_WITH_CAPTIONS_ON, {
          video_id: videoId,
          captions_visible: captionsVisibilityInStorage,
        });
      } else {
        analytics.track(VIDEO_STARTED_WITH_CAPTIONS_OFF, {
          video_id: videoId,
          captions_visible: captionsVisibilityInStorage,
        });
      }
    }

    // If we fetch captions after the user first starts playing the video
    if (!prevProps.captionsSourceUrl && captionsSourceUrl && hasStarted) {
      const captionsVisibilityInStorage = getShowCaptions();

      if (captionsVisibilityInStorage) {
        analytics.track(VIDEO_STARTED_WITH_CAPTIONS_ON, {
          video_id: videoId,
          captions_visible: captionsVisibilityInStorage,
        });
      } else {
        analytics.track(VIDEO_STARTED_WITH_CAPTIONS_OFF, {
          video_id: videoId,
          captions_visible: captionsVisibilityInStorage,
        });
      }
    }

    // track noise suppression value at video play time
    if (!prevProps.hasStarted && hasStarted) {
      analytics.track(VIDEO_START_NOISE_SUPPRESSION, {
        video_id: videoId,
        noise_suppression: this.props.noiseSuppression,
      });
    }

    if (this.player) {
      this.resizeObserver.observe(this.player);
    }
  }

  componentDidMount() {
    const { videoModel, updateCaptionsVisibleNoSideEffects } = this.props;

    this.props.videoPlayerHideComments(false);

    if (videoModel.complete) {
      this.props.updateVideoIframeVisibility(true);
    }

    VIDEO_PLAYER_PRELOAD_ICONS.map(icon => {
      imageUtils.createImage(icon);
    });
    document.addEventListener('keyup', this.onKeyUp, false);
    document.addEventListener('keydown', this.onKeyDown, false);
    window.addEventListener('message', this.handlePlayerControlEvents);

    listenForFullscreen(this.onFullscreenChange);
    this.setupMouseInactiveTimeout();

    updateCaptionsVisibleNoSideEffects(getShowCaptions());

    this.props.getFeatureFlag(ROLLOUT_VIDEO_PLAYER_END_SCREEN);
    this.props.getFeatureFlag(ROLLOUT_WEAVE);

    this.setupResizeObserver();

    if (this.player) {
      this.resizeObserver.observe(this.player);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('message', this.handlePlayerControlEvents);

    stopListeningForFullscreen(this.onFullscreenChange);
    this.resizeObserver.disconnect();
  }

  setupResizeObserver = () => {
    // Flatten getBoundingClientRect so it's not a DOMRect
    const rect = this.player ? this.player.getBoundingClientRect() : {};

    this.setState({
      containerDimensions: {
        width: rect.width || 0,
        height: rect.height || 0,
        // this is from the perspective of the video player, so it's always 0
        x: 0,
      },
    });

    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        // Not all browsers support contentBoxSize, so we check for it first.
        if (entry.contentBoxSize) {
          // Firefox implements `contentBoxSize` as a single content rect, rather than an array
          const contentBoxSize = Array.isArray(entry.contentBoxSize)
            ? entry.contentBoxSize[0]
            : entry.contentBoxSize;

          this.setState({
            containerDimensions: {
              x: 0,
              width: contentBoxSize.inlineSize,
              height: contentBoxSize.blockSize,
            },
          });
        } else {
          this.setState({
            containerDimensions: {
              x: 0,
              width: entry.contentRect.width,
              height: entry.contentRect.height,
            },
          });
        }
      }
    });
  };

  updateVideoPlayerMouseInactiveImmediately = () => {
    window.clearTimeout(this.mouseInactiveTimeout);
    this.props.updateVideoPlayerMouseInactive();
  };

  handlePlayerControlEvents = e => {
    if (!this.player) {
      return;
    }

    const { data: eventData = {} } = e;

    const volumeUpAmount = 0.1;
    const volumeDownAmount = 0.1;

    switch (eventData.type) {
      case messages.EMBED_VIDEO_PLAY_PAUSE:
        if (this.props.paused) {
          this.props.requestVideoPlayerPlay();
        } else {
          this.props.requestVideoPlayerPause();
        }

        break;

      // Fast scrubbing
      case messages.EMBED_VIDEO_JUMP_BACK:
        VideoSession.sendEvent('pause', {
          currentTime: this.props.currentTime,
        });
        this.props.requestVideoPlayerCurrentTime(
          this.props.currentTime - PLAYER_LONG_STEP
        );
        break;
      case messages.EMBED_VIDEO_JUMP_FORWARD:
        VideoSession.sendEvent('pause', {
          currentTime: this.props.currentTime,
        });
        this.props.requestVideoPlayerCurrentTime(
          this.props.currentTime + PLAYER_LONG_STEP
        );
        break;
      case messages.EMBED_VIDEO_MUTE:
        this.props.muted
          ? this.props.requestVideoPlayerUnmute()
          : this.props.requestVideoPlayerMute();

        break;
      // Fine scrubbing
      case messages.EMBED_VIDEO_STEP_FORWARD:
        this.props.requestVideoPlayerCurrentTime(this.props.currentTime + 1);
        break;
      case messages.EMBED_VIDEO_STEP_BACK:
        this.props.requestVideoPlayerCurrentTime(this.props.currentTime - 1);
        break;

      case messages.EMBED_VIDEO_TOGGLE_FULLSCREEN:
        this.props.fullScreen
          ? this.props.requestVideoPlayerLeaveFullScreen()
          : this.props.requestVideoPlayerEnterFullScreen();
        break;

      case messages.EMBED_VIDEO_TOGGLE_THEATER: {
        const { currentUserIsOwner, theaterMode, inTrimMode } = this.props;
        const newTheaterModeVal = inTrimMode ? false : !theaterMode;

        document.cookie = `${KEY_THEATER_MODE} = ${newTheaterModeVal};`;

        this.props.updateTheaterMode(newTheaterModeVal, this.props.inTrimMode);
        analytics.track(THEATER_MODE_TOGGLED, {
          currentUserIsOwner,
          on: newTheaterModeVal,
        });
        break;
      }
      case messages.EMBED_VIDEO_VOLUME_UP:
        this.props.volume + volumeUpAmount <= 1 &&
          this.props.requestVideoPlayerVolume(
            this.props.volume + volumeUpAmount
          );
        break;
      case messages.EMBED_VIDEO_VOLUME_DOWN:
        this.props.volume - volumeDownAmount >= 0 &&
          this.props.requestVideoPlayerVolume(
            this.props.volume - volumeDownAmount
          );
        break;
      case messages.EMBED_VIDEO_TOGGLE_SPEED:
        rateToggler(this.props.playbackRate);
        break;

      default:
        break;
    }
  };
}

const mapStateToProps = state => {
  const {
    autoplay,
    captionsDetails,
    captionsVisible,
    chromePipEnabled,
    current_time: currentTime,
    controlsActive,
    duration,
    ended,
    flipped_camera: flippedCamera,
    from_link_expand: fromLinkExpand,
    from_public_share_page: fromPublicSharePage,
    full_screen: fullScreen,
    has_started: hasStarted,
    is_fetching: isFetching,
    is_inline_embed_on_loom: isInlineEmbedOnLoom,
    is_source_requested: sourceRequested,
    isMainVideoOnSharePage,
    muted,
    needs_password: needsPassword,
    parent_location: parentLocation,
    playback_rate: playbackRate,
    password,
    paused,
    poster_url: posterUrl,
    request_current_time: requestCurrentTime,
    request_enter_full_screen: requestEnterFullScreen,
    request_leave_full_screen: requestLeaveFullScreen,
    request_mute: requestMute,
    request_pause: requestPause,
    request_play: requestPlay,
    request_playback_rate: requestPlaybackRate,
    request_unmute: requestUnmute,
    request_volume: requestVolume,
    seeking,
    source_mime_type: sourceMimeType,
    source_url: sourceUrl,
    theater_mode: theaterMode,
    video,
    viewer_needs_permission: needsPermission,
    volume,
    waiting,
    transcriptVisible,
    videoReadyToPlayOnSharePage,
    show_all_comments: showingAllComments,
    userCanAccessTranscript,
  } = state.video_player;

  const {
    current_user_is_owner: currentUserIsOwner,
    cta_enabled: ctaEnabled,
    iframe_visible: iframeVisible,
    transcript_details: { transcription_status: transcriptStatus },
  } = state.share_video;

  const { user } = state;
  const { is_logged_in: isLoggedIn, is_logged_in_checked: isLoggedInChecked } =
    user;

  // We check user.current explicitly to make sure the user is logged in.
  const needsIntentSurvey =
    user.current && !user.current.persona?.persona_v1?.complete;

  const {
    current_trim_range: currentTrimRange,
    in_trim_mode: inTrimMode,
    trim_ranges: trimRanges,
    playRanges,
    selectedPlayRangeHandle,
    selectedPlayRangeIdx,
  } = state.trimming;

  const {
    trim_ranges: lastSavedTrimRanges,
    noise_cancellation_type: noiseSuppression,
  } = video.processing_information;

  const shouldNudgePlayRange =
    inTrimMode && selectedPlayRangeIdx != null && selectedPlayRangeHandle;

  const videoId = video.id;
  const isCameraRecording = (video.video_properties || {}).screen_type === CAM;

  const waitingForTrim = waitingForTrimUtil(video, state);

  const commentsEnabled = video.comments_enabled;

  const recordReplyEnabled = video.record_reply_enabled;

  const isV6Video = video?.video_properties?.recording_version === V6;

  // autoplay if the flag is set and the current user
  // is not the owner
  // or autoplay if video is a V6 video and video is in trim mode
  const shouldAutoplay =
    (autoplay && !video.current_user_is_owner) ||
    (autoplay && isV6Video && inTrimMode);
  const { isActive: isViewerInsightsActive } = state.viewerInsights;

  const { cta_flow: ctaFlowActive } = state.editor;

  const { captionsSourceUrl } = captionsDetails;

  const rolloutNewEndScreen =
    (state.feature_flags[ROLLOUT_VIDEO_PLAYER_END_SCREEN] || {}).value ===
    'variant';

  const rolloutWeave = VARIANT.includes(
    (state.feature_flags[ROLLOUT_WEAVE] || {}).value
  );

  return {
    autoplay: shouldAutoplay,
    captionsSourceUrl,
    captionsVisible,
    chromePipEnabled,
    controlsActive,
    currentUserIsOwner,
    ctaEnabled,
    ctaFlowActive,
    commentsEnabled,
    recordReplyEnabled,
    currentTime,
    currentTrimRange,
    duration,
    ended,
    flippedCamera,
    fromLinkExpand,
    fromPublicSharePage,
    fullScreen,
    hasStarted: hasStarted || inTrimMode,
    iframeVisible,
    inTrimMode,
    isCameraRecording,
    isFetching,
    isInlineEmbedOnLoom,
    isLoggedIn: isLoggedIn && isLoggedInChecked,
    isMainVideoOnSharePage,
    lastSavedTrimRanges,
    muted,
    needsIntentSurvey,
    needsPassword,
    needsPermission,
    parentLocation,
    password,
    paused,
    playbackRate,
    playRanges,
    posterUrl,
    requestCurrentTime,
    requestEnterFullScreen,
    requestLeaveFullScreen,
    requestMute,
    requestPause,
    requestPlay,
    requestPlaybackRate,
    requestUnmute,
    requestVolume,
    seeking,
    selectedPlayRangeIdx,
    selectedPlayRangeHandle,
    shouldNudgePlayRange,
    sourceMimeType,
    sourceRequested,
    sourceUrl,
    theaterMode,
    trimRanges,
    videoId,
    videoModel: video,
    videoName: video.name,
    volume,
    waiting,
    waitingForTrim,
    isViewerInsightsActive,
    transcriptVisible,
    activeVideoTranscriptId: video.active_video_transcript_id,
    transcriptStatus,
    videoReadyToPlayOnSharePage,
    userCanAccessTranscript,
    showingAllComments,
    rolloutNewEndScreen,
    rolloutWeave,
    areVideoCtaSettingsOpen: state.editor?.cta_flow,
    areVideoSettingsOpen: state.editor?.video_settings_flow,
    areViewerInsightsOpen: selectIsActive(state),
    areThumbnailSettingsOpen: state.editor?.thumbnail_flow,
    canSplitPlayRange: selectCanSplitPlayRange(state),
    canDeletePlayRange: selectCanDeletePlayRange(state),
    noiseSuppression,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    getFeatureFlag: featureFlag => dispatch(getFeatureFlagCreator(featureFlag)),
    requestVideoPlayerMute: () => dispatch(requestVideoPlayerMute()),
    requestVideoPlayerUnmute: () => dispatch(requestVideoPlayerUnmute()),
    requestVideoPlayerCurrentTime: currentTime =>
      dispatch(requestVideoPlayerCurrentTime(currentTime)),
    requestVideoPlayerPause: () => dispatch(requestVideoPlayerPause()),
    requestVideoPlayerPlay: () => dispatch(requestVideoPlayerPlay()),
    requestVideoPlayerEnterFullScreen: () =>
      dispatch(requestVideoPlayerEnterFullScreen()),
    requestVideoPlayerLeaveFullScreen: () =>
      dispatch(requestVideoPlayerLeaveFullScreen()),
    requestVideoPlayerVolume: volume =>
      dispatch(requestVideoPlayerVolume(volume)),
    trackVideoWatchDurationCreator: timeWatched =>
      dispatch(trackVideoWatchDurationCreator(timeWatched)),
    trackVideoViewCreator: () => dispatch(trackVideoViewCreator()),
    updateVideoPlayerBufferedParts: bufferedParts =>
      dispatch(updateVideoPlayerBufferedParts(bufferedParts)),
    updateVideoPlayerCurrentTime: currentTime =>
      dispatch(updateVideoPlayerCurrentTime(currentTime)),
    updateVideoPlayerDimensionsCreator: (width, height) =>
      dispatch(updateVideoPlayerDimensionsCreator(width, height)),
    updateVideoPlayerDurationCreator: duration =>
      dispatch(updateVideoPlayerDurationCreator(duration)),
    updateVideoPlayerEnded: () => dispatch(updateVideoPlayerEnded()),
    updateVideoPlayerEnterFullScreen: () =>
      dispatch(updateVideoPlayerEnterFullScreen()),
    updateVideoPlayerLeaveFullScreen: () =>
      dispatch(updateVideoPlayerLeaveFullScreen()),
    updateVideoPlayerMouseInactive: () =>
      dispatch(updateVideoPlayerMouseInactive()),
    updateVideoPlayerMute: () => dispatch(updateVideoPlayerMute()),
    updateVideoPlayerNotWaiting: () => dispatch(updateVideoPlayerNotWaiting()),
    updateVideoPlayerPasswordCreator: password =>
      dispatch(updateVideoPlayerPasswordCreator(password)),
    updateVideoPlayerPaused: () => dispatch(updateVideoPlayerPaused()),
    updateVideoPlayerPlay: () => dispatch(updateVideoPlayerPlay()),
    updateVideoPlayerPlaying: () => dispatch(updateVideoPlayerPlaying()),
    updateVideoPlayerPlaybackRate: playbackRate =>
      dispatch(updateVideoPlayerPlaybackRate(playbackRate)),
    updateVideoPlayerSourceCreator: () =>
      dispatch(updateVideoPlayerSourceCreator()),
    updateVideoPlayerSourceMp4Creator: () =>
      dispatch(updateVideoPlayerSourceMp4Creator()),
    updateVideoPlayerSourceRawCreator: () =>
      dispatch(updateVideoPlayerSourceRawCreator()),
    updateVideoPlayerUnmute: () => dispatch(updateVideoPlayerUnmute()),
    updateVideoPlayerVolume: volume =>
      dispatch(updateVideoPlayerVolume(volume)),
    updateVideoPlayerWaiting: () => dispatch(updateVideoPlayerWaiting()),
    videoPlayerHideComments: () => dispatch(videoPlayerHideComments()),
    updateTheaterMode: (theaterMode, inTrimMode) =>
      dispatch(updateTheaterMode(theaterMode, inTrimMode)),
    updateVideoIframeVisibility: () => dispatch(updateVideoIframeVisibility()),
    enterCommentMode: () => dispatch(videoPlayerEnterCommentMode()),
    handleToggleTranscriptCreator: () =>
      dispatch(handleToggleTranscriptCreator()),
    updateCaptionsVisible: isVisible =>
      dispatch(handleUpdateCaptionsVisible(isVisible)),
    updateCaptionsVisibleNoSideEffects: isVisible =>
      dispatch(toggleCaptionsVisible(isVisible)),
    toggleViewerInsightsUI: () => dispatch(toggleViewerInsightsUI()),
    endVideoSettingsFlow: () => dispatch(endVideoSettingsFlow()),
    clearCTAWidgetState: () => dispatch(clearCTAWidgetState()),
    endThumbnailFlow: () => dispatch(endThumbnailFlow()),
    skipForward: seconds => dispatch(skipForward(seconds)),
    skipBack: seconds => dispatch(skipBack(seconds)),
    updatePlayRanges: playRanges =>
      dispatch(updatePlayRangesCreator(playRanges)),
    updateSelectedPlayRangeHandle: handle =>
      dispatch(setSelectedPlayRangeHandle(handle)),
    splitPlayRange: () => dispatch(splitPlayRangeCreator()),
    deletePlayRange: () => dispatch(deletePlayRangeCreator()),
    updateShareVideoInTrimMode: inTrimMode =>
      dispatch(updateShareVideoInTrimMode(inTrimMode)),
    enterTrimMode: trimRanges => dispatch(enterTrimModeCreator(trimRanges)),
    watchAgain: () => dispatch(watchAgain()),
  };
};

const ConnectedVideoPlayer = connect(
  mapStateToProps,
  mapDispatchToProps
)(VideoPlayer);

export default function VideoPlayerWithContext(props) {
  return (
    <EmbedContext.Consumer>
      {({ hideEmbedTopBar }) => (
        <ConnectedVideoPlayer {...props} hideEmbedTopBar={hideEmbedTopBar} />
      )}
    </EmbedContext.Consumer>
  );
}
